import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  signal,
  viewChild
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, animationFrameScheduler, fromEvent } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  tap,
  throttleTime,
  withLatestFrom
} from 'rxjs/operators';
import { MatSidenav } from '@angular/material/sidenav';
import { MatListItem } from '@angular/material/list';
import { NavigationStart, Router } from '@angular/router';
import { EventService } from '../../services/event.service';
import { SIDEBAR_LINKS } from '../../constants/layout.constants';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { SharedModule } from '../../modules/shared.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPermissionsModule } from 'ngx-permissions';
import { BaseComponent } from '../../abstract-components/base.component';
import { DOCUMENT } from '@angular/common';
import { DashboardService } from '~core/services/dashboard.service';

@Component({
  selector: 'content-wrapper',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatSidenavModule,
    MatTooltipModule,
    SharedModule,
    MatListModule,
    NgxPermissionsModule
  ],
  templateUrl: './content-wrapper.component.html',
  styleUrls: ['./content-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentWrapperComponent extends BaseComponent implements OnInit {
  private _breakpointObserver = inject(BreakpointObserver);
  private _eventService = inject(EventService);
  private _router = inject(Router);
  private _sanitizer = inject(DomSanitizer);
  private _appRef = inject(ApplicationRef);
  private _dashboardService = inject(DashboardService);
  private _document = inject(DOCUMENT);

  drawer = viewChild<MatSidenav>(MatSidenav);
  toolbar = viewChild<ElementRef<HTMLElement>>('toolbar');
  listItems = viewChild<QueryList<MatListItem>>('listItems');

  isSmallScreen$: Observable<boolean> = this._breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  isWindowAtTop$ = fromEvent(window, 'scroll').pipe(
    takeUntilDestroyed(this._destroyRef),
    startWith(false),
    throttleTime(0, animationFrameScheduler),
    map(() => window.scrollY === 0),
    distinctUntilChanged()
  );

  isLayoutHidden$ = this._globalFacade.routeData$.pipe(
    map(routeData =>
      routeData ? routeData.data?.layoutHidden === true : false
    ),
    startWith(true)
  );

  closeSmallScreenSidebarOnRouteChange$ = this._router.events.pipe(
    withLatestFrom(this.isSmallScreen$),
    filter(([a, b]) => b && a instanceof NavigationStart),
    tap(() => this.drawer()?.close())
  );

  onToggleMenu$ = this._eventService.events$.pipe(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    filter(ev => ev.type === 'TOGGLE_MENU'),
    withLatestFrom(this.isSmallScreen$),
    tap(() => this.drawer()?.toggle())
  );

  sidebarLinks = SIDEBAR_LINKS;
  isSidenavCollapsed = false;
  isLoadingSupersetOAuthData = signal(false);

  ngOnInit(): void {
    this._authFacade.resetLoginState();
    this._authFacade.resetOnboardingForms();
    this._authFacade.hydrateLoggedUserProfile();
    this._dashboardFacade.resetState();
  }

  toggleSidenav(): void {
    this.isSidenavCollapsed = !this.isSidenavCollapsed;
    setTimeout(() => {
      this._appRef.tick();
    }, 100);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }

  navigateToSuperset(): void {
    this.isLoadingSupersetOAuthData.set(true);
    this._dashboardService
      .getSuperSetOAuthUrl()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: url => {
            this._document.defaultView?.open(url, '_blank');
          },
          finalize: () => {
            this.isLoadingSupersetOAuthData.set(false);
          }
        })
      )
      .subscribe();
  }

  navigateToPortal(): void {
    this._document.defaultView?.open(
      this._constantsService.environment.postalDomainBasePathUrl,
      '_blank',
      'noopener noreferrer'
    );
  }

  get toolbarHeight(): number | undefined {
    if (!this.toolbar()?.nativeElement) return;
    return this.toolbar()?.nativeElement.clientHeight || 0;
  }
}
